// Public snapshot payload for `lot:{lotId}:snapshot` — the per-lot
// realtime channel introduced in ADR 011 §2 (PR-D4 of BL-461).
//
// ADR023 (BL-645 + BL-670): the projection carries the lot's decision-stamp
// INPUTS, never a derived `status` label. Clients re-derive the lot's
// lifecycle phase via `deriveLotPhase` (the shared IsomorphicService) on
// their per-second tick, so a snapshot can no longer go stale just because
// the clock advanced (ADR023 Decision 4) — a lot whose close time passes
// derives `awaiting-resolution`/`sold` on the client's own tick with no new
// broadcast. BL-645 narrowed the auction snapshot + added these stamps to the
// lot snapshot but temporarily KEPT `status` for the grid; BL-670 completes
// the lot read plane — `status` is dropped from the payload, the DB column,
// and every realtime consumer (the grid hook `useRealtimeLotUpdate`
// derives phase via `deriveLotPhase`; the live-clerk channel reads terminal
// state via `deriveTerminalDisposition`).
//
// PUBLIC-SAFE per the §5-amendment data-sensitivity picker. The decision
// stamps are public timestamps (the same posture as the retired `status`).
// The topic's only catch-up endpoints are bidder-web's (single and batch),
// gated on "lot exists" only.
//
// Wire shape carries `{ topicId, version, snapshot }` post-2026-06-10
// (see ADR 011 §2 amendment) — `version` is the sole ordering key.
//
// BL-2021: the money fields ride as MoneyWire in the lot's own
// `auction_lots.currency` (ADR 060 — Money Type), encoded here with the shared
// `encodeRowMoney` by BOTH the live publisher and the catch-up routes, so the
// two deliveries are byte-identical (ADR 011 — Durable Events). Clients decode
// once, with `decodeLotSnapshot`, in the hook that reads the topic.
//
// BL-2105: the lot's START PRICE joined the projection (decision D1, Michael
// Lawrie 2026-10-01). Every bidder surface feeds it into the live next-bid
// ladder beside `acceptedBidAmount` and `increment`, and it stays editable until
// the first bid, so an SSR / REST copy was the rule #32 "live value from a
// snapshot field plus an off-snapshot input" shape. It is realtime-only on the
// bidder now: `PublicLot` and `LotCardRowDto` no longer carry it.
//
// BL-2359: the lot's ACREAGE and PRICING BASIS joined it (BL-2352 decision D1,
// Michael Lawrie 2026-10-08). Six bidder surfaces multiply the live price by the
// acreage or label it with the basis ("Total $X", "/acre"), and an Admin can
// correct acreage on a lot with bids, so a page-load copy went stale. Both are
// realtime-only on the bidder now; `PublicLot` and `LotCardRowDto` no longer
// carry them (ADR 035 — Money Units § 6).
//
// BUG-1910: no reserve VALUE is on this topic — no amount. The one reserve
// word it carries is `reserveBadge` (BL-2169 / BL-2203), computed server-side on
// the house's opt-in; met / not met only for a house that picked it — the named
// carve-out in architecture.md. The tri-state `reserveMet` (BUG-793) used to ride
// here to every (even anonymous) browser, and watching it flip as bids landed
// bracketed the seller's confidential reserve to within one increment. The
// reserve and its verdict ride ONLY the authenticated admin lot-state stream
// (`admin-lot-state.ts`), for the clerk console.
//
// BUG-2114: no terminal DISPOSITION is on this topic either. A closed lot
// carries one `closedAt` stamp whether it sold or not. `hammeredAt` used to ride
// here beside `closeProcessedAt`, and because the close sells any lot whose max
// covers its reserve, "closed, not hammered, with bids" told any anonymous
// reader the reserve was not met. Sold / No Sale rides the admin stream only.

import type { LotReserveBadge } from '@auctogon/db/types/enums';
import type Decimal from 'decimal.js';

import type { LotPricingType } from '../../isomorphic/land';
import { deriveLotClosedAt } from '../../isomorphic/lifecycle/derive-lot-phase';
import type { MoneyWire } from '../../isomorphic/money/codec';
import type { Money } from '../../isomorphic/money/money';
import { decodeWireAmount, encodeIncrementOverride, encodeRowMoney } from './money-wire-amount';

/**
 * Full snapshot — the lot's realtime-relevant state at a point in time.
 * Mirrors the typed columns on the `lot_snapshot` table (migrations
 * `20260609204923_create_lot_snapshot.sql` +
 * `20260630200702_narrow_snapshot_projections_to_decision_stamps_bl645.sql` +
 * the BL-670 migration that dropped the `status` column).
 *
 * - `acceptedBidAmount` — the lot's live "highest accepted bid", projected
 *   from `auction_lots.accepted_bid_unit_amount_denormalized` (a trigger-maintained
 *   FK-derived field = `MAX(bids.amount) WHERE status='accepted'`, BL-626).
 *   This is bidder-web's canonical live-price source. `null` = no accepted bid
 *   yet; consumers MUST use `value == null` guards, never truthiness.
 *   (BL-1519 removed the `hammerPriceDeprecated` sibling that used to sit
 *   here, projecting `auction_lots.hammer_price`. Do NOT re-add it: the hammer
 *   is the recorded SALE price, so it is NULL for a lot's entire live phase and
 *   carries nothing this channel needs. `acceptedBidAmount` is the live price
 *   for every consumer, admin and bidder alike. A sold lot's price belongs to
 *   the REST surfaces that own post-sale money, per rule #32 — putting it back
 *   on the snapshot would create the second live delivery path that rule
 *   exists to prevent.)
 * - `closeTime` — the lot's scheduled close timestamp. Mutates on anti-snipe
 *   extensions; the countdown re-anchors when a fresh value publishes (RAC1).
 * - `currentBidderCustomerId` — the current high bidder's `customers.id`
 *   (BL-690: switched from the retired `customer_user.id`; matches
 *   `bids.customer_id` + `auction_lots.winner_customer_id`, and is non-null for
 *   walk-in/clerk bidders who have no login). Projected from the trigger-
 *   maintained `auction_lots.accepted_bid_customer_id_denormalized`. `null`
 *   until the first bid. Drives the "you are the highest bidder" affordance —
 *   consumers compare it to their OWN `customers.id` (`user.customerId`).
 * - `startPrice` — `auction_lots.start_price`, as MoneyWire (BL-2105). The
 *   next-bid ladder's basis on a fresh lot; per-acre on a per-acre land lot
 *   (ADR 035 — Money Units). Never null on a real row — `null` only on
 *   {@link EMPTY_LOT_SNAPSHOT}, the no-row default, which consumers treat as
 *   "not known yet", never as zero.
 * - `increment` — per-lot bid-increment override, as MoneyWire. `null`
 *   means "fall through to the auction's tier table." A zero or negative
 *   column value (the admin console's "clear override" sentinel) maps to
 *   `null` here, once, so no client re-implements that rule (BL-2021 D2).
 * - `bidsPlacedCount` — the BL-588 carve-out projection
 *   (`denormalized-counts-and-fields.md` §5). `0` is valid; consumers use
 *   `> 0` guards. (BUG-1910 removed the `reserveMet` sibling — see the header.)
 * - The decision stamps (`pausedAt` … `readiedAt`) — the predicate inputs
 *   `deriveLotPhase` reads, with the two terminal-outcome stamps folded into
 *   one `closedAt` (BUG-2114). All `null` until the corresponding decision
 *   occurs. NOTE (BL-645): the projection carries only public-safe TIMESTAMPS.
 *   `winner_customer_id` (buyer identity) is deliberately NOT projected — it's
 *   return-metadata on `deriveLotPhase`, not a phase predicate, and this
 *   snapshot rides the public/anon bidder channel. `deriveLotPhase` treats it
 *   as optional and reads `null` for it here. (BL-687: the withdrawal-actor
 *   UUID that used to sit alongside it was dropped from `auction_lots`
 *   entirely — the actor lives in the audit log.)
 */
export interface LotSnapshot {
    /** Live "highest accepted bid" (BL-626), in the lot's currency. Bidder-web's canonical live price. `null` = no accepted bid. */
    acceptedBidAmount: MoneyWire | null;
    closeTime: string | null;
    currentBidderCustomerId: string | null;
    /** The lot's start price in its currency (BL-2105). `null` only on the no-row default. */
    startPrice: MoneyWire | null;
    /** Per-lot increment override in the lot's currency; `null` = use the auction's tier table. */
    increment: MoneyWire | null;
    /**
     * Placed-bid count — `auction_lots.bids_placed_count_denormalized`
     * (BL-588 carve-out, `denormalized-counts-and-fields.md` §5). `0` is
     * valid; consumers MUST use `value > 0` gating, never truthiness.
     */
    bidsPlacedCount: number;
    // ── ADR023 (BL-645) decision-stamp inputs — `deriveLotPhase` reads these.
    //    Public-safe timestamps only; identity UUIDs are NOT projected. ──
    pausedAt: string | null;
    withdrawnAt: string | null;
    /**
     * BUG-2114 — when the lot closed, sold or not: `deriveLotClosedAt` over
     * `close_processed_at` / `hammered_at`. The two stamps used to ride here
     * separately, and `hammeredAt` (set only on a sale) told a bidder a lot
     * that closed unsold with bids missed its reserve. The disposition rides
     * ONLY the authenticated admin lot-state stream. `null` until close.
     */
    closedAt: string | null;
    deletedAt: string | null;
    biddingOpenedAt: string | null;
    preBiddingAt: string | null;
    readiedAt: string | null;
    /**
     * BL-2169 / BL-2203 — the ONE reserve word this public topic carries:
     * `'reserve'` ("Has reserve"), `'lifted'` ("Reserve lifted!"), or — only on
     * the house's met / not-met pick — `'not_met'` / `'met'`. Computed
     * server-side by `computeReserveBadge` from the lot's own row; never the
     * amount. `null` = no badge, and always `null` once the lot is closed.
     */
    reserveBadge: LotReserveBadge | null;
    /**
     * BL-2359 — `auction_lots.acreage` as the exact decimal string the `numeric`
     * column holds (the admin lot-state stream's encoding, BL-1554). A quantity,
     * not money, so it is not a MoneyWire. `null` on a non-land lot and on the
     * no-row default ("not known yet"), never `"0"`.
     */
    acreage: string | null;
    /**
     * BL-2359 — `auction_lots.pricing_type`: `'per_acre'` makes every price a
     * per-acre price ("/acre", and a whole-tract Total via `extendLandHammer`);
     * `'total'` is a lump sum. `null` on a non-land lot.
     */
    pricingType: LotPricingType | null;
}

/**
 * Input row shape — duck-typed to match the Drizzle SELECT the
 * `publishLotSnapshot` helper uses when reading the source `auction_lots`
 * row. Listing only the fields we read keeps callers free to project
 * wider rows without breaking the mapper.
 */
export interface LotSnapshotRow {
    /** `auction_lots.accepted_bid_unit_amount_denormalized` (BL-626) → `acceptedBidAmount`. */
    acceptedBidAmount: Decimal | string | null;
    /**
     * The lot's `auction_lots.currency` — the denomination of both money fields
     * (BL-2021). Never projected as a field of its own: it rides inside each
     * MoneyWire.
     */
    currency: string;
    closeTime: string | null;
    currentBidderCustomerId: string | null;
    /** `auction_lots.start_price` (BL-2105) — a `moneyNumeric` Decimal (BL-2239), or a raw-SQL read's exact string. */
    startPrice: Decimal | string;
    /** `auction_lots.increment` — a `moneyNumeric` Decimal (BL-2239), or a raw-SQL read's exact string. */
    increment: Decimal | string | null;
    bidsPlacedCount: number;
    // ── ADR023 (BL-645) decision-stamp inputs from `auction_lots` (public-
    //    safe timestamps only; identity UUIDs are NOT projected) ──
    pausedAt: string | null;
    withdrawnAt: string | null;
    hammeredAt: string | null;
    // NOTE: `passedAt` was retired by BL-1387 PR 2 (see `LotSnapshot` above).
    closeProcessedAt: string | null;
    deletedAt: string | null;
    biddingOpenedAt: string | null;
    preBiddingAt: string | null;
    readiedAt: string | null;
    /** BL-2169 — the badge word, already computed (the publisher) or stored (`lot_snapshot.reserve_badge`). */
    reserveBadge: LotReserveBadge | null;
    /** BL-2359 — `auction_lots.acreage` / `lot_snapshot.acreage` (a `numeric` decoded as its exact string). */
    acreage: string | null;
    /** BL-2359 — `auction_lots.pricing_type` / `lot_snapshot.pricing_type`. */
    pricingType: LotPricingType | null;
}

/**
 * Map a lot source row to the public snapshot shape. Carries the decision-
 * stamp inputs (the client derives phase via `deriveLotPhase`) — no derived
 * `status` label since BL-670.
 *
 * Projects the trigger-maintained `accepted_bid_unit_amount_denormalized` to
 * `acceptedBidAmount` — the live price for every consumer since BL-1519
 * retired the `hammerPriceDeprecated` projection.
 *
 * @param row - The source row, its amounts as the driver decoded them.
 * @returns The public snapshot, money as MoneyWire in `row.currency`.
 * @throws Error when `row.currency` is not a supported currency (`encodeRowMoney`).
 */
export function mapLotSnapshot(row: LotSnapshotRow): LotSnapshot {
    const { currency } = row;
    return {
        acceptedBidAmount:
            row.acceptedBidAmount == null ? null : encodeRowMoney({ amount: row.acceptedBidAmount, currency }),
        closeTime: row.closeTime,
        currentBidderCustomerId: row.currentBidderCustomerId,
        startPrice: encodeRowMoney({ amount: row.startPrice, currency }),
        increment: encodeIncrementOverride(row.increment, currency),
        bidsPlacedCount: row.bidsPlacedCount,
        pausedAt: row.pausedAt,
        withdrawnAt: row.withdrawnAt,
        closedAt: deriveLotClosedAt(row),
        deletedAt: row.deletedAt,
        biddingOpenedAt: row.biddingOpenedAt,
        preBiddingAt: row.preBiddingAt,
        readiedAt: row.readiedAt,
        reserveBadge: row.reserveBadge,
        acreage: row.acreage,
        pricingType: row.pricingType,
    };
}

/**
 * Default snapshot returned by the catch-up endpoint when no row exists
 * for the requested lot. Mirrors a freshly-created lot's projection so the
 * consumer's typed read doesn't crash on optional chaining. All decision
 * stamps `null` (no decision yet → `deriveLotPhase` reads `draft`);
 * `bidsPlacedCount: 0`.
 */
export const EMPTY_LOT_SNAPSHOT: LotSnapshot = {
    acceptedBidAmount: null,
    closeTime: null,
    currentBidderCustomerId: null,
    startPrice: null,
    increment: null,
    bidsPlacedCount: 0,
    pausedAt: null,
    withdrawnAt: null,
    closedAt: null,
    deletedAt: null,
    biddingOpenedAt: null,
    preBiddingAt: null,
    readiedAt: null,
    reserveBadge: null,
    acreage: null,
    pricingType: null,
};

/** The lot snapshot's money-bearing fields. */
export const LOT_SNAPSHOT_MONEY_FIELDS = ['acceptedBidAmount', 'startPrice', 'increment'] as const;
type LotSnapshotMoneyField = (typeof LOT_SNAPSHOT_MONEY_FIELDS)[number];

/**
 * A lot snapshot as the client holds it: every money field decoded into `Money`
 * once, in the hook that reads the topic (BL-2021), so no component re-parses
 * an amount or pairs it with a currency from elsewhere.
 */
export type LotSnapshotDecoded = Omit<LotSnapshot, LotSnapshotMoneyField> & {
    [K in LotSnapshotMoneyField]: Money | null;
};

/** How one lot snapshot decoded. */
export type LotSnapshotDecodeResult =
    | { kind: 'ok'; snapshot: LotSnapshotDecoded }
    /** The field that was not a well-formed MoneyWire — the caller logs and skips the frame. */
    | { kind: 'undecodable'; field: LotSnapshotMoneyField };

/**
 * Decode a lot snapshot's money fields with the shared `decodeWireAmount`
 * (ADR 044 — Must Consolidate: one decode rule for every realtime money
 * payload). `null` stays `null`. Never throws.
 *
 * @param snapshot - The snapshot as it arrived (broadcast or catch-up).
 * @returns The decoded snapshot, or `undecodable` naming the first malformed field.
 */
export function decodeLotSnapshot(snapshot: LotSnapshot): LotSnapshotDecodeResult {
    const money = {} as { [K in LotSnapshotMoneyField]: Money | null };
    for (const field of LOT_SNAPSHOT_MONEY_FIELDS) {
        const wire = snapshot[field];
        if (wire == null) {
            money[field] = null;
            continue;
        }
        const decoded = decodeWireAmount(wire);
        if (decoded.kind !== 'money') return { kind: 'undecodable', field };
        money[field] = decoded.money;
    }
    return { kind: 'ok', snapshot: { ...snapshot, ...money } };
}
